import { useContext } from 'react';
import { GridColumns, GridRows } from '@visx/grid';
import { DataContext } from '@visx/xychart';

import type { GridProps } from '../../types/GridProps';
import { useChartContext } from '../../utils/useChartContext';

/**
 * Grid component for composable LineChart.
 *
 * Thin wrapper around visx's Grid component with LaunchPad-specific defaults.
 *
 * @example
 * ```tsx
 * <LineChart>
 *   <Grid rows columns />
 *   <Series ... />
 * </LineChart>
 * ```
 */
export function Grid({ columns = false, rows = true, lineStyle = 'dashed', numTicks = 5, ...rest }: GridProps) {
  const { yTickValues } = useChartContext();
  const dataContext = useContext(DataContext);

  const strokeDasharray = lineStyle === 'dashed' ? '5,5' : lineStyle === 'dotted' ? '2,3' : undefined;
  const gridLineStyles = dataContext?.theme?.gridStyles;

  // Don't render if we don't have the necessary scales and dimensions
  if (
    !dataContext ||
    !dataContext.xScale ||
    !dataContext.yScale ||
    typeof dataContext.innerWidth !== 'number' ||
    typeof dataContext.innerHeight !== 'number'
  ) {
    return null;
  }

  // Type narrowing - we've confirmed these are numbers above
  const { xScale, yScale, innerWidth, innerHeight, margin } = dataContext;

  return (
    <>
      {rows && (
        <GridRows
          left={margin?.left}
          scale={yScale}
          width={innerWidth}
          tickValues={yTickValues}
          numTicks={yTickValues ? undefined : numTicks}
          strokeDasharray={strokeDasharray}
          lineStyle={gridLineStyles}
          {...rest}
        />
      )}
      {columns && (
        <GridColumns
          top={margin?.top}
          scale={xScale}
          height={innerHeight}
          numTicks={numTicks}
          strokeDasharray={strokeDasharray}
          lineStyle={gridLineStyles}
          {...rest}
        />
      )}
    </>
  );
}
